import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { isEqual } from 'lodash';

import { FaultsStore } from '@shared/state';
import { FetchFaultAnalysisParams } from '@shared/models';

import { FaultAnalysisSectionComponent } from './section';
import { FaultHistoryComponent } from './fault-history';
import { BRCBaseComponent } from '@brc/hrt-base-component';
import { FaultAnalysisFiltersComponent } from './filters/fault-analysis-filters.component';
import { PredictiveAlertsComponent } from './predictive-alerts/predictive-alert.component';

export const FAULT_ANALYSIS_SESSION_STORAGE_KEY = 'faultAnalysisFilters';

@Component({
  selector: 'gea-hrt-fault-analysis',
  styleUrls: ['./fault-analysis.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [FaultAnalysisFiltersComponent, FaultAnalysisSectionComponent, FaultHistoryComponent, PredictiveAlertsComponent],
  template: `
    <gea-hrt-fault-filters />
    <div class="fault-analysis-container">
      <gea-hrt-predictive-alerts />
      <gea-hrt-fault-analysis-section [annunciations$]="shutdowns$" type="shutdown" />
      <gea-hrt-fault-analysis-section [annunciations$]="warnings$" type="warning" />
      <gea-hrt-fault-history />
    </div>
  `,
})
export class FaultAnalysisComponent extends BRCBaseComponent {
  private readonly faultsStore = inject(FaultsStore);

  readonly shutdowns$ = toObservable(this.faultsStore.shutdowns);
  readonly warnings$ = toObservable(this.faultsStore.warnings);

  private readonly faultAnalysisRequest = computed<FetchFaultAnalysisParams | undefined>(
    () => {
      const machines = this.faultsStore.selectedMachines();
      const faults = this.faultsStore.selectedFaults();
      const dateRange = this.faultsStore.selectedDateRange();
      const faultStatus = this.faultsStore.selectedFaultStatus();

      if (!machines || !faults || !dateRange) {
        return undefined;
      }

      return { machines, faults, faultStatus, dateRange };
    },
    { equal: isEqual }
  );

  constructor() {
    super();

    effect(() => {
      const request = this.faultAnalysisRequest();

      if (!request) {
        return;
      }

      if (!request.machines.length || !request.faults?.length) {
        this.faultsStore.resetFaultAnalysis();
      } else {
        this.faultsStore.fetchFaultAnalysis(request);
      }
    });
  }
}
